import { useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import CustomDatePicker from '@/components/CustomDatePicker';
import GuestSelector from '@/components/GuestSelector';
import {
  useBaseSearchForm,
  UseBaseSearchFormOptions,
} from '@/hooks/search/useBaseSearchForm';

import { useSearchSubmit } from '@/hooks/search/useSearchSubmit';

type Variant = 'desktop' | 'mobile';

interface BaseSearchFormProps extends UseBaseSearchFormOptions {
  variant?: Variant;
  className?: string;
  showTitle?: boolean;
  maxGuests?: number;
  unavailableDates?: Date[];
  onSubmitted?: (payload: {
    checkIn?: Date;
    checkOut?: Date;
    guests?: string;
  }) => void;
}

const addDaysLocal = (date: Date, days: number) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

const BaseSearchForm = ({
  variant,
  className = '',
  showTitle = true,
  maxGuests = 7,
  unavailableDates = [],
  onSubmitted,
  initialCheckIn,
  initialCheckOut,
  initialGuests,
  onDateChange,
  onGuestsChange,
}: BaseSearchFormProps) => {
  const isMobileDevice = useIsMobile();

  const renderMobile = useMemo(
    () => (variant ? variant === 'mobile' : isMobileDevice),
    [variant, isMobileDevice],
  );

  const {
    checkIn,
    checkOut,
    guests,
    hasValidDateRange,
    handleCheckIn,
    handleCheckOut,
    handleGuests,
  } = useBaseSearchForm({
    initialCheckIn,
    initialCheckOut,
    initialGuests,
    onDateChange,
    onGuestsChange,
  });

  const { submit } = useSearchSubmit();

  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleCheckInInternal = (date: Date | undefined) => {
    handleCheckIn(date);
    if (!renderMobile && date) setCheckoutOpen(true);
  };

  const handleCheckOutInternal = (date: Date | undefined) => {
    handleCheckOut(date);
    setCheckoutOpen(false);
  };

  const doSubmit = () => {
    const payload = { checkIn, checkOut, guests };
    submit(payload);
    onSubmitted?.(payload);
  };

  if (renderMobile) {
    return (
      <div className={`md:hidden space-y-4 ${className}`}>
        {showTitle && (
          <h2 className="text-xl font-semibold text-gray-900">
            Reserva tu estadía
          </h2>
        )}

        <div className="grid grid-cols-2 border border-gray-300 rounded-xl overflow-hidden">
          <div className="p-4 border-r border-gray-300">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              ENTRADA
            </div>
            <CustomDatePicker
              selected={checkIn}
              onSelect={handleCheckInInternal}
              placeholder="Agregar fecha"
              className="border-0 p-0 h-auto bg-transparent text-base text-gray-600"
              unavailableDates={unavailableDates}
            />
          </div>
          <div className="p-4">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              SALIDA
            </div>
            <CustomDatePicker
              selected={checkOut}
              onSelect={handleCheckOutInternal}
              placeholder="Agregar fecha"
              className="border-0 p-0 h-auto bg-transparent text-base text-gray-600"
              minDate={checkIn}
              unavailableDates={unavailableDates}
            />
          </div>
        </div>

        <div className="border border-gray-300 rounded-xl p-4">
          <div className="text-xs font-semibold text-gray-700 mb-2">
            HUÉSPEDES
          </div>
          <GuestSelector
            value={guests ?? '1'}
            onChange={handleGuests}
            maxGuests={maxGuests}
            className="border-0 p-0 h-auto bg-transparent text-base text-gray-600"
            placeholder="1 huésped"
          />
        </div>

        <Button
          onClick={doSubmit}
          className="w-full h-12 text-white rounded-xl bg-brand-primary hover:bg-brand-primary-hover"
          disabled={!hasValidDateRange}
        >
          Buscar
        </Button>
      </div>
    );
  }

  return (
    <div className={`hidden md:block ${className}`}>
      {showTitle && (
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Reserva tu estadía
        </h2>
      )}

      <div className="grid grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Entrada
          </label>
          <CustomDatePicker
            selected={checkIn}
            onSelect={handleCheckInInternal}
            placeholder="Agregar fecha"
            unavailableDates={unavailableDates}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Salida
          </label>
          <CustomDatePicker
            selected={checkOut}
            onSelect={handleCheckOutInternal}
            placeholder="Agregar fecha"
            open={checkoutOpen}
            onOpenChange={setCheckoutOpen}
            unavailableDates={unavailableDates}
            minDate={checkIn ? addDaysLocal(checkIn, 1) : new Date()}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Huéspedes
          </label>
          <GuestSelector
            value={guests ?? '1'}
            onChange={handleGuests}
            maxGuests={maxGuests}
            className="h-12"
            placeholder="Huéspedes"
          />
        </div>

        <div className="flex items-end">
          <Button
            onClick={doSubmit}
            className="w-full h-12 text-white rounded-xl bg-brand-primary hover:bg-brand-primary-hover"
            disabled={!hasValidDateRange}
          >
            Buscar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BaseSearchForm;
